import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { livePolling } from '@/app/query-client'
import { INDEX_INTRADAY_AVAILABLE, marketService } from '@/features/market/api/marketService'
import { marketKeys } from '@/features/market/api/keys'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type { DailyBars } from '@/features/market/api/marketService'
import type {
  IndexIntraday,
  IndexRange,
  IndexSummary,
  IndicesPage,
  MarketMood,
  MoodDay,
} from '@/schemas/market'

/**
 * The NEPSE card: value, change, breadth and the intraday series.
 *
 * Polling is gated on market status exactly as the company list is, so a tab
 * left open overnight issues no requests at all — the index cannot move while
 * NEPSE is shut, and the backend only refreshes on a 60s cron anyway.
 *
 * `range` (the chip row) and `indexKey` (which row of the indices table is
 * selected) both live in the key, and both change from a click rather than a
 * navigation — `keepPreviousData` keeps the old figure and chart painted
 * while the new one loads instead of dropping the card to a skeleton on
 * every chip tap or row switch.
 */
export const indexSummaryQueryOptions = (range: IndexRange, indexKey = 'NEPSE') =>
  queryOptions<IndexSummary>({
    queryKey: marketKeys.indexSummary(range, indexKey),
    queryFn: () => marketService.indexSummary(range, indexKey),
  })

export const useIndexSummary = (range: IndexRange, indexKey = 'NEPSE') => {
  const { isOpen } = useMarketStatus()

  return useQuery<IndexSummary>({
    ...indexSummaryQueryOptions(range, indexKey),
    refetchInterval: livePolling(isOpen),
    placeholderData: keepPreviousData,
  })
}

/**
 * Today's intraday series for the 1D chart. Disabled — no request at all — until
 * `INDEX_INTRADAY_AVAILABLE` says mm-be serves it; polled on the live cadence once it does.
 */
export const indexIntradayQueryOptions = (indexKey = 'NEPSE') =>
  queryOptions<IndexIntraday | null>({
    queryKey: marketKeys.indexIntraday(indexKey),
    queryFn: () => marketService.indexIntraday(indexKey),
    enabled: INDEX_INTRADAY_AVAILABLE,
  })

export const useIndexIntraday = (indexKey = 'NEPSE', active = true) => {
  const { isOpen } = useMarketStatus()

  return useQuery<IndexIntraday | null>({
    ...indexIntradayQueryOptions(indexKey),
    enabled: INDEX_INTRADAY_AVAILABLE && active,
    refetchInterval: livePolling(isOpen),
  })
}

/**
 * The market mood, mm-be's Fear & Greed composite (`marketService.mood`). Reads the
 * `/market/summary/` entry the NEPSE card already holds, so it adds no request; polled as that
 * card is.
 */
export const marketMoodQueryOptions = () =>
  queryOptions<MarketMood | null>({
    queryKey: marketKeys.mood(),
    queryFn: ({ client }) => marketService.mood(client),
  })

export const useMarketMood = (): UseQueryResult<MarketMood | null> => {
  const { isOpen } = useMarketStatus()

  return useQuery({ ...marketMoodQueryOptions(), refetchInterval: livePolling(isOpen) })
}

/**
 * The mood's last `days` sessions. One reading per session, scored in the evening, so it is not
 * polled: it changes once a day at most.
 */
export const moodHistoryQueryOptions = (days: number) =>
  queryOptions<MoodDay[]>({
    queryKey: marketKeys.moodHistory(days),
    queryFn: () => marketService.moodHistory(days),
  })

/**
 * The four NEPSE-wide indices table — the `/market/indices` route's MAIN
 * data: the loader awaits it, so the chart card and the table both mount
 * with it already cached. No search param keys this page (the selected row
 * is local `useState`), so it takes the plain query shape Market ·
 * Overview and Stock do, not the keepPreviousData one the other five
 * families in this pass need.
 *
 * The factory carries the key and the fetch only; polling stays in the hook
 * because it is gated on `useMarketStatus()`, a hook.
 */
export const indicesQueryOptions = () =>
  queryOptions<IndicesPage>({
    queryKey: marketKeys.indices(),
    queryFn: () => marketService.indices(),
  })

/**
 * The same query for `IndicesChartCard` and `IndicesTable`, which read it for
 * themselves. Not a suspense read: under the route's loader their data is
 * already there on mount, so the loading and failed branches they still carry
 * are unreachable on `/market/indices` — kept because both components (and
 * their tests) still depend on this exact result type, the same reasoning
 * `useMarketOverview` documents for Market · Overview's cards.
 */
export const useIndices = (): UseQueryResult<IndicesPage> => {
  const { isOpen } = useMarketStatus()

  return useQuery<IndicesPage>({
    ...indicesQueryOptions(),
    refetchInterval: livePolling(isOpen),
  })
}

/**
 * A symbol's daily OHLCV bars — the stock Technical panel's whole input.
 *
 * Not polled. These are DAILY bars: the newest one is today's session so far,
 * and every indicator computed from it is a 14-to-200-session window in which
 * one partial session moves nothing a member would notice. The screens that
 * poll (`useIndexSummary`, the live tables) are showing a price that changes
 * by the minute; this is showing a year.
 *
 * `enabled` is passed through because the panel that reads it is one tab of
 * thirteen, and the other twelve must not fetch a year of bars to render a
 * "not available yet" block.
 */
export const dailyBarsQueryOptions = (symbol: string, countback?: number) =>
  queryOptions<DailyBars>({
    queryKey: marketKeys.dailyBars(symbol, countback),
    // `({ client })` rather than a bare thunk: React Query hands the queryFn
    // the client that OWNS this query, so the endpoint cache underneath
    // (`api/endpointCache.ts`) uses that one instead of reaching past it to the
    // module singleton. `endpointCache`'s own comment names this as the right
    // wiring and the optional argument as the seam for it; this is the first
    // hook to use it, and it is what keeps a test's fresh client actually
    // fresh — otherwise one test's bars are served to the next from a cache no
    // test owns.
    queryFn: ({ client }) => marketService.dailyBars(symbol, countback, client),
  })

export const useDailyBars = (
  symbol: string,
  countback?: number,
  options: { enabled?: boolean } = {},
): UseQueryResult<DailyBars> =>
  useQuery<DailyBars>({ ...dailyBarsQueryOptions(symbol, countback), ...options })
